import React, { lazy, Suspense, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { isPreview, useGetUser } from './helpers';
import NavigationProvider from './context/Navigation';
import { useTypedSelector } from './redux/hooks';
import { PolyglotProvider } from './context/Polyglot';
import { FallbackContainer } from './modals/FallbackContainer';
import { AppLoader } from './modals/AppLoader';
import { ErrorBoundary } from './components';
import AppCrash from './components/AppCrash';
import AppForceUpdater from './modals/AppForceUpdater';
import { useAppContext } from './context/AppContext/AppContext';
import { selectAccountId } from './redux/slices';

const Home = lazy(() => import('./pages/home/Home'));
const Auth = lazy(() => import('./pages/auth/Auth'));

interface AppProps { }

const App: React.FC<AppProps> = () => {
    const { appName, appFavicon } = useAppContext();
    const accountId = useTypedSelector(selectAccountId);

    const has_users = useTypedSelector(state => Array.isArray(state.auth?.users));
    const has_specific_user = useTypedSelector(state => state.user.name && state.user.password);

    const login_and_specific = has_users && has_specific_user;

    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const { userIsLoading, isTokenLoggingIn, isTokenLoginError, isGetUserError } = useGetUser();

    const dataLoading = userIsLoading && isTokenLoggingIn;

    if (window.location.pathname.includes('/x')) {
        window.location.replace('/');
        return <span>Redirecting...</span>;
    }

    return (
        <PolyglotProvider>
            <HelmetProvider>
                <NavigationProvider>
                    <ErrorBoundary errorComponent={<AppCrash />}>
                        <FallbackContainer>
                            <Helmet defer={false}>
                                <title>
                                    {appName}
                                    {isPreview ? ' - Preview' : ''}
                                </title>
                                <link rel='icon' href={appFavicon} />
                            </Helmet>
                            <Suspense>
                                {login_and_specific ? <Home setIsLoaded={setIsLoaded} /> : null}
                                {!login_and_specific && !dataLoading ? (
                                    <Auth
                                        setIsLoaded={setIsLoaded}
                                        isTokenLoginError={isTokenLoginError}
                                        isGetUserError={isGetUserError}
                                    />
                                ) : null}
                            </Suspense>
                            <AppLoader userIsLoading={dataLoading} isLoaded={isLoaded && (typeof accountId === "number" || !has_specific_user)} />
                            <AppForceUpdater />
                        </FallbackContainer >
                    </ErrorBoundary >
                </NavigationProvider >
            </HelmetProvider >
        </PolyglotProvider >
    );
};

export default App;
